/**
 * Post a work-log update against a task. Mirrors the mobile
 * "Post update" sheet (`app/(app)/projects/[id]/task/[taskId].tsx`
 * lines 813–1132).
 *
 * Writes to `tasks/{taskId}/updates/{updateId}` via `addTaskUpdate`
 * (web port of mobile). Auto-rolls progress + status onto the
 * parent task.
 *
 * Fields:
 *   - Progress (0/25/50/75/100 shortcuts + slider) — required
 *   - Note (textarea) — optional
 *   - Photos — deferred (R2 upload helper not yet on web).
 *     Mobile users can attach photos; web users see a placeholder
 *     until the R2 web upload pipeline ships.
 */
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import { uploadFilesToR2 } from '@/lib/r2Upload';
import { addTaskUpdate } from '@/lib/tasks';
import type { TaskItem } from '@/lib/useProjectTasks';

export function LogWorkSlideOver({
  open,
  onClose,
  task,
  defaultDisplayName,
}: {
  open: boolean;
  onClose: () => void;
  task: TaskItem | null;
  /** Used as `authorName` when the auth user has no display name. */
  defaultDisplayName?: string;
}) {
  const { user } = useAuth();
  const [progress, setProgress] = useState<number>(0);
  const [note, setNote] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Photo staging — keep `File` objects in memory until submit, then
  // upload to R2 in parallel. Preview URLs are revoked on cleanup.
  const [photos, setPhotos] = useState<{ file: File; preview: string }[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Seed progress from the task's current value when the modal opens.
  useEffect(() => {
    if (!open) return;
    setProgress(task?.progress ?? 0);
    setNote('');
    setError(null);
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.preview));
      return [];
    });
  }, [open, task]);

  // Revoke any leftover object URLs on unmount to avoid leaks.
  useEffect(() => {
    return () => {
      setPhotos((prev) => {
        prev.forEach((p) => URL.revokeObjectURL(p.preview));
        return [];
      });
    };
  }, []);

  const addPhotos = (files: FileList | null) => {
    if (!files) return;
    const next: { file: File; preview: string }[] = [];
    for (const f of Array.from(files)) {
      if (!f.type.startsWith('image/')) continue;
      next.push({ file: f, preview: URL.createObjectURL(f) });
    }
    if (next.length === 0) return;
    setPhotos((prev) => [...prev, ...next].slice(0, 6));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePhoto = (i: number) => {
    setPhotos((prev) => {
      const out = [...prev];
      const [removed] = out.splice(i, 1);
      if (removed) URL.revokeObjectURL(removed.preview);
      return out;
    });
  };

  // Body scroll lock + Esc.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, busy, onClose]);

  const handleSubmit = async () => {
    setError(null);
    if (!user) return setError('Sign-in required.');
    if (!task) return setError('No task selected.');
    setBusy(true);
    try {
      // Upload any attached photos to R2 first — same kind ('task_update')
      // mobile uses, same callable, same bucket. Public URLs are
      // written into the update doc's `photoUris`.
      let photoUrls: string[] = [];
      if (photos.length > 0) {
        photoUrls = await uploadFilesToR2({
          files: photos.map((p) => p.file),
          kind: 'task_update',
          refId: task.id,
          orgId: task.orgId,
        });
      }
      await addTaskUpdate(task.id, {
        authorId: user.uid,
        authorName:
          user.displayName ||
          defaultDisplayName ||
          user.email ||
          'Member',
        progress,
        text: note.trim(),
        photoUris: photoUrls,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const SHORTCUTS = [0, 25, 50, 75, 100];

  return (
    <>
      <div
        className={`slide-overlay${open ? ' open' : ''}`}
        onClick={() => !busy && onClose()}
      />
      <div className={`slide-over${open ? ' open' : ''}`}>
        <div className="slide-header">
          <h2 className="slide-title">
            <i className="ph ph-check-circle" /> Post Update
          </h2>
          <button
            type="button"
            className="slide-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div className="slide-body">
          {task ? (
            <div
              style={{
                background: 'var(--surface-muted, #FAFAFA)',
                borderRadius: 8,
                padding: 12,
                border: '1px solid var(--border)',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--text-tertiary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  marginBottom: 4,
                }}
              >
                Logging work for
              </div>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                }}
              >
                {task.title}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--text-tertiary)',
                  marginTop: 4,
                }}
              >
                Currently at {task.progress ?? 0}%
              </div>
            </div>
          ) : null}

          {error ? (
            <div
              style={{
                background: 'var(--critical-bg)',
                color: 'var(--critical-text)',
                padding: 10,
                borderRadius: 6,
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {/* Progress shortcuts */}
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                display: 'block',
                marginBottom: 8,
              }}
            >
              Progress · {progress}%
            </label>
            <div
              style={{
                display: 'flex',
                gap: 6,
                marginBottom: 10,
              }}
            >
              {SHORTCUTS.map((s) => {
                const active = s === progress;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setProgress(s)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      border: active
                        ? '1.5px solid var(--brand)'
                        : '1px solid var(--border)',
                      background: active ? 'var(--brand-bg)' : '#fff',
                      color: active ? 'var(--brand)' : 'var(--text-primary)',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: active ? 700 : 500,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {s}%
                  </button>
                );
              })}
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={progress}
              onChange={(e) => setProgress(parseInt(e.currentTarget.value, 10))}
              style={{ width: '100%' }}
            />
          </div>

          {/* Note */}
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Note
            </label>
            <textarea
              style={{
                width: '100%',
                minHeight: 90,
                padding: '10px 12px',
                border: '1px solid var(--border)',
                borderRadius: 6,
                fontSize: 13,
                color: 'var(--text-primary)',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
              placeholder="What did you complete? Any blockers?"
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
            />
          </div>

          {/* Photo attachments */}
          <div>
            <label
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-tertiary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                display: 'block',
                marginBottom: 8,
              }}
            >
              Photos · {photos.length}
              {photos.length >= 6 ? ' (max)' : ` of 6`}
            </label>
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {photos.map((p, i) => (
                <div
                  key={p.preview}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid var(--border)',
                  }}
                >
                  <img
                    src={p.preview}
                    alt="Attachment"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(i)}
                    disabled={busy}
                    aria-label="Remove photo"
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 20,
                      height: 20,
                      borderRadius: 10,
                      background: 'rgba(0,0,0,0.6)',
                      color: '#fff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                  >
                    <i className="ph-bold ph-x" />
                  </button>
                </div>
              ))}
              {photos.length < 6 ? (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                  title="Attach photos"
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 6,
                    border: '1px dashed var(--border)',
                    background: '#FAFAFA',
                    color: 'var(--brand)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    fontFamily: 'inherit',
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                >
                  <i className="ph ph-plus" style={{ fontSize: 18 }} />
                  Add
                </button>
              ) : null}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => addPhotos(e.currentTarget.files)}
            />
            <div
              style={{
                fontSize: 10,
                color: 'var(--text-tertiary)',
                marginTop: 6,
              }}
            >
              Photos upload to R2 when you post the update. Up to 6 per
              entry. JPG / PNG / HEIC supported.
            </div>
          </div>

          {/* Actions */}
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
              paddingTop: 16,
              borderTop: '1px solid var(--border)',
            }}
          >
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleSubmit}
              disabled={busy}
            >
              {busy
                ? photos.length > 0
                  ? `Uploading ${photos.length} photo${photos.length === 1 ? '' : 's'}…`
                  : 'Posting…'
                : 'Post update'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
